import { useEffect } from "react";
import { X, FileDown, Truck, Sparkles, Mail, Clock, Loader2, Palette } from "lucide-react";

/**
 * Modal for picking how to print a Buddy:
 *  - $3 outline-only Coloring Page (impulse buy, instant download)
 *  - $5 instant PDF (digital print bundle — buddy + recipe)
 *  - $14.99 laminated mat + magnet set (physical print)
 *
 * Each digital tier supports TWO actions:
 *   - "Get it now" → instant Stripe checkout (single item)
 *   - "Add to cart" → adds to the global cart (for batched multi-item checkout)
 *
 * Props:
 *   open: boolean
 *   buddy: { id, name, image, ... }
 *   onClose: () => void
 *   onPickDigital: (buddy) => void       // $5 instant Stripe checkout
 *   onPickColoring: (buddy) => void      // $3 instant Stripe checkout
 *   onPickPhysical: (buddy) => void      // $14.99 add to cart
 *   onAddDigital: (buddy) => void        // OPTIONAL — $5 to cart
 *   onAddColoring: (buddy) => void       // OPTIONAL — $3 to cart
 *   loadingDigital: boolean
 *   loadingColoring: boolean
 */
export default function PrintOptionsModal({
  open,
  buddy,
  onClose,
  onPickDigital,
  onPickColoring,
  onPickPhysical,
  onAddDigital,
  onAddColoring,
  loadingDigital = false,
  loadingColoring = false,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !buddy) return null;

  return (
    <div
      data-testid="print-options-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-pb-ink/60 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-white rounded-[2rem] shadow-[0_30px_80px_rgba(28,22,40,0.35)] overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="relative px-6 sm:px-8 pt-6 pb-4 bg-gradient-to-br from-pb-cream via-white to-pb-sun/30 border-b border-pb-sky/20">
          <button
            data-testid="btn-close-print-modal"
            onClick={onClose}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/70 hover:bg-white text-pb-ink flex items-center justify-center border border-pb-sky/30 transition"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-4">
            {buddy.image && (
              <div className="h-16 w-16 rounded-2xl overflow-hidden bg-white shadow-md flex-shrink-0 border-2 border-white">
                <img src={buddy.image} alt={buddy.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-coral">
                Print {buddy.name}
              </p>
              <h2 className="font-display font-black text-2xl sm:text-3xl text-pb-ink leading-tight mt-0.5">
                How do you want it?
              </h2>
            </div>
          </div>
        </div>

        {/* Three options */}
        <div className="px-5 sm:px-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* $3 Coloring Page */}
          <div className="flex flex-col">
            <button
              data-testid="opt-coloring-page"
              onClick={() => onPickColoring(buddy)}
              disabled={loadingColoring}
              className="group relative text-left rounded-[1.5rem] border-2 border-pb-sky/40 hover:border-pb-coral bg-white p-4 hover:-translate-y-1 hover:shadow-[0_18px_36px_rgba(242,106,52,0.18)] transition disabled:opacity-60 disabled:hover:translate-y-0 flex-1"
            >
            <div className="h-11 w-11 rounded-xl bg-pb-sun text-pb-ink flex items-center justify-center shadow-md">
              {loadingColoring ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Palette className="h-5 w-5" />
              )}
            </div>
            <h3 className="font-display font-black text-lg text-pb-ink mt-3 leading-tight">
              Coloring page
            </h3>
            <p className="font-display font-black text-2xl text-pb-ink mt-1">
              $3
            </p>
            <p className="text-[13px] text-pb-soft mt-1.5 leading-snug">
              Black & white outline of your Buddy — print and let your kid bring it to life with crayons.
            </p>
            <ul className="mt-2.5 space-y-1 text-[11px] text-pb-ink/80">
              <li className="flex items-center gap-1.5">
                <Clock className="h-3 w-3 text-pb-coral" /> Instant PDF
              </li>
              <li className="flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-pb-coral" /> Sized for 7&quot; plate
              </li>
              <li className="flex items-center gap-1.5">
                <Mail className="h-3 w-3 text-pb-coral" /> Emailed too
              </li>
            </ul>
            <span className="block mt-3 text-center text-[11px] font-extrabold uppercase tracking-widest text-pb-ink group-hover:text-pb-coral group-hover:underline">
              Get it now →
            </span>
            </button>
            {onAddColoring && (
              <button
                data-testid="opt-coloring-page-add-cart"
                onClick={() => onAddColoring(buddy)}
                className="mt-2 rounded-full text-[11px] font-extrabold uppercase tracking-widest text-pb-soft hover:text-pb-coral hover:underline py-1.5"
              >
                + add to cart to bundle
              </button>
            )}
          </div>

          {/* $5 Digital Bundle (FEATURED) */}
          <div className="flex flex-col">
            <button
              data-testid="opt-digital-print"
              onClick={() => onPickDigital(buddy)}
              disabled={loadingDigital}
              className="group relative text-left rounded-[1.5rem] border-2 border-pb-coral bg-gradient-to-br from-white via-pb-cream to-pb-sun/40 p-4 hover:-translate-y-1 hover:shadow-[0_18px_36px_rgba(242,106,52,0.28)] transition disabled:opacity-60 disabled:hover:translate-y-0 flex-1"
            >
            <span className="absolute -top-2 -right-2 bg-pb-coral text-white text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full shadow-md">
              Most popular
            </span>
            <div className="h-11 w-11 rounded-xl bg-pb-coral text-white flex items-center justify-center shadow-md">
              {loadingDigital ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <FileDown className="h-5 w-5" />
              )}
            </div>
            <h3 className="font-display font-black text-lg text-pb-ink mt-3 leading-tight">
              Buddy + recipe
            </h3>
            <p className="font-display font-black text-2xl text-pb-coral mt-1">
              $5
            </p>
            <p className="text-[13px] text-pb-soft mt-1.5 leading-snug">
              Full-color Buddy PDF + a coordinated kid-friendly recipe card. Print, fridge it, lunchbox it.
            </p>
            <ul className="mt-2.5 space-y-1 text-[11px] text-pb-ink/80">
              <li className="flex items-center gap-1.5">
                <Clock className="h-3 w-3 text-pb-coral" /> Instant — 30 seconds
              </li>
              <li className="flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-pb-coral" /> Sized for 7&quot; plate
              </li>
              <li className="flex items-center gap-1.5">
                <Mail className="h-3 w-3 text-pb-coral" /> Emailed + redownloadable
              </li>
            </ul>
            <span className="block mt-3 text-center text-[11px] font-extrabold uppercase tracking-widest text-pb-coral group-hover:underline">
              Get it now →
            </span>
            </button>
            {onAddDigital && (
              <button
                data-testid="opt-digital-print-add-cart"
                onClick={() => onAddDigital(buddy)}
                className="mt-2 rounded-full text-[11px] font-extrabold uppercase tracking-widest text-pb-soft hover:text-pb-coral hover:underline py-1.5"
              >
                + add to cart to bundle
              </button>
            )}
          </div>

          {/* $14.99 Physical */}
          <div className="flex flex-col">
            <button
              data-testid="opt-physical-print"
              onClick={() => onPickPhysical(buddy)}
              className="group text-left rounded-[1.5rem] border-2 border-pb-sky/40 hover:border-pb-ink bg-white p-4 hover:-translate-y-1 hover:shadow-[0_18px_36px_rgba(28,22,40,0.15)] transition flex-1"
            >
            <div className="h-11 w-11 rounded-xl bg-pb-ink text-white flex items-center justify-center shadow-md">
              <Truck className="h-5 w-5" />
            </div>
            <h3 className="font-display font-black text-lg text-pb-ink mt-3 leading-tight">
              Laminated set
            </h3>
            <p className="font-display font-black text-2xl text-pb-ink mt-1">
              $14.99
            </p>
            <p className="text-[13px] text-pb-soft mt-1.5 leading-snug">
              Shipped to you — laminated Plate Buddy mat + matching fridge magnet. The real deal.
            </p>
            <ul className="mt-2.5 space-y-1 text-[11px] text-pb-ink/80">
              <li className="flex items-center gap-1.5">
                <Truck className="h-3 w-3 text-pb-ink" /> Ships free in the US
              </li>
              <li className="flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-pb-ink" /> Wipe-clean lamination
              </li>
              <li className="flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-pb-ink" /> Doubles as a placemat
              </li>
            </ul>
            <span className="block mt-3 text-center text-[11px] font-extrabold uppercase tracking-widest text-pb-ink group-hover:text-pb-coral group-hover:underline">
              Add to cart →
            </span>
            </button>
          </div>
        </div>

        <div className="px-6 pb-6 pt-1 text-center">
          <p className="text-[11px] text-pb-soft">
            All prints sized for a standard 7&quot; kid&apos;s plate · No account needed · Secure Stripe checkout
          </p>
        </div>
      </div>
    </div>
  );
}
